import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Terminal, Code, Database, Settings, Zap, Activity, Shield, ArrowLeft, Cpu, FileText, PlayCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import SystemPromptEditor from '@/components/agent-control/SystemPromptEditor';
import ToolConfigurator from '@/components/agent-control/ToolConfigurator';
import MemoryManager from '@/components/agent-control/MemoryManager';
import VoiceSettings from '@/components/agent-control/VoiceSettings';
import EdgeFunctionMonitor from '@/components/agent-control/EdgeFunctionMonitor';
import DirectTerminal from '@/components/agent-control/DirectTerminal';
import DirectChat from '@/components/agent-control/DirectChat';
import SecurityInfo from '@/components/agent-control/SecurityInfo';
import AgentStatusDashboard from '@/components/agent-control/AgentStatusDashboard';
import FunctionExecutionLogs from '@/components/agent-control/FunctionExecutionLogs';
import TestingSandbox from '@/components/agent-control/TestingSandbox';

export default function AgentControl() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('status');
  const [isPlatformOwner, setIsPlatformOwner] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        toast.error("Access Denied: Authentication required");
        navigate('/auth');
      } else {
        checkPlatformOwnerAccess();
      }
    }
  }, [authLoading, user]);

  const checkPlatformOwnerAccess = async () => {
    try {
      if (!user) return;

      // Check if user has platform owner role (owner role with NULL org_id)
      // This is the ONLY way to be a platform admin - must have 'owner' role in user_roles with org_id = NULL
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role, org_id')
        .eq('user_id', user.id)
        .eq('role', 'owner')
        .is('org_id', null);

      if (error) throw error;

      const hasAccess = roles && roles.length > 0;
      setIsPlatformOwner(hasAccess);

      if (!hasAccess) {
        toast.error("Access Denied: Platform Owner privileges required");
        navigate('/');
      }
    } catch (error: any) {
      console.error('Access check error:', error);
      toast.error("Access check failed. Redirecting to safety.");
      setIsPlatformOwner(false);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-primary animate-pulse" />
          <p className="text-muted-foreground">Verifying platform owner access...</p>
        </div>
      </div>
    );
  }

  if (!isPlatformOwner) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-3 border-b pb-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/')}
            className="mr-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Shield className="w-8 h-8 text-primary" />
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-foreground">Agent Control Panel</h1>
            <p className="text-muted-foreground mt-1">Platform Owner Access - Direct meta agent configuration</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
            <Activity className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Level 4.5 Meta Agent</span>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap gap-2 justify-start">
            <TabsTrigger value="status" className="flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              Status
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Chat
            </TabsTrigger>
            <TabsTrigger value="terminal" className="flex items-center gap-2">
              <Terminal className="w-4 h-4" />
              Terminal
            </TabsTrigger>
            <TabsTrigger value="sandbox" className="flex items-center gap-2">
              <PlayCircle className="w-4 h-4" />
              Sandbox
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Logs
            </TabsTrigger>
            <TabsTrigger value="prompt" className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              Prompt
            </TabsTrigger>
            <TabsTrigger value="tools" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Tools
            </TabsTrigger>
            <TabsTrigger value="memory" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              Memory
            </TabsTrigger>
            <TabsTrigger value="voice" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Voice
            </TabsTrigger>
            <TabsTrigger value="monitor" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Monitor
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Security
            </TabsTrigger>
          </TabsList>

          <TabsContent value="status" className="mt-6">
            <AgentStatusDashboard />
          </TabsContent>

          <TabsContent value="chat" className="mt-6">
            <DirectChat />
          </TabsContent>

          <TabsContent value="terminal" className="mt-6">
            <DirectTerminal />
          </TabsContent>

          <TabsContent value="sandbox" className="mt-6">
            <TestingSandbox />
          </TabsContent>

          <TabsContent value="logs" className="mt-6">
            <FunctionExecutionLogs />
          </TabsContent>

          <TabsContent value="prompt" className="mt-6">
            <SystemPromptEditor />
          </TabsContent>

          <TabsContent value="tools" className="mt-6">
            <ToolConfigurator />
          </TabsContent>

          <TabsContent value="memory" className="mt-6">
            <MemoryManager />
          </TabsContent>

          <TabsContent value="voice" className="mt-6">
            <VoiceSettings />
          </TabsContent>

          <TabsContent value="monitor" className="mt-6">
            <EdgeFunctionMonitor />
          </TabsContent>

          <TabsContent value="security" className="mt-6">
            <SecurityInfo />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
